import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { gsap } from 'gsap';

export const AdminLoginPage: React.FC = () => {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
    const [authStep, setAuthStep] = useState<string>('');

    // Refs for GSAP
    const containerRef = useRef<HTMLDivElement>(null);
    const emblemRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // If already authenticated, redirect to dashboard
    useEffect(() => {
        if (user) {
            navigate('/admin/dashboard');
        }
    }, [user, navigate]);

    // Canvas starry particle effect
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let width = (canvas.width = window.innerWidth);
        let height = (canvas.height = window.innerHeight);

        const handleResize = () => {
            if (!canvas) return;
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', handleResize);

        // Particle nodes
        const particles: { x: number; y: number; vx: number; vy: number; radius: number; alpha: number }[] = [];
        const numParticles = Math.min(55, Math.floor(width / 25));

        for (let i = 0; i < numParticles; i++) {
            particles.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.4,
                vy: (Math.random() - 0.5) * 0.4,
                radius: Math.random() * 1.8 + 0.6,
                alpha: Math.random() * 0.5 + 0.2,
            });
        }

        const render = () => {
            ctx.clearRect(0, 0, width, height);

            // Draw glowing particles
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                p.x += p.vx;
                p.y += p.vy;

                if (p.x < 0) p.x = width;
                if (p.x > width) p.x = 0;
                if (p.y < 0) p.y = height;
                if (p.y > height) p.y = 0;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(16, 185, 129, ${p.alpha})`;
                ctx.shadowBlur = 8;
                ctx.shadowColor = '#10b981';
                ctx.fill();

                // Draw constellation connection lines
                for (let j = i + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const dx = p.x - p2.x;
                    const dy = p.y - p2.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < 110) {
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.strokeStyle = `rgba(16, 185, 129, ${0.15 * (1 - dist / 110)})`;
                        ctx.lineWidth = 0.6;
                        ctx.stroke();
                    }
                }
            }

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Initial GSAP Intro Animation
    useEffect(() => {
        const emblem = emblemRef.current;
        const card = cardRef.current;
        const title = titleRef.current;

        if (!emblem || !card || !title) return;

        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        tl.fromTo(emblem, 
            { scale: 0.6, opacity: 0, filter: 'blur(12px)' }, 
            { scale: 1, opacity: 1, filter: 'blur(0px)', duration: 1.1 }
        )
        .fromTo(title,
            { y: 25, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.8 },
            '-=0.6'
        )
        .fromTo(card,
            { y: 35, opacity: 0, scale: 0.96 },
            { y: 0, opacity: 1, scale: 1, duration: 0.9, ease: 'back.out(1.15)' },
            '-=0.5'
        );
    }, []);

    // Submit handler with dramatic high-tech verification sequence
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsAuthenticating(true);

        try {
            // High-tech authentication telemetry sequence
            setAuthStep('INITIALIZING SECURE HANDSHAKE...');
            await new Promise(r => setTimeout(r, 350));

            setAuthStep(`AUTHENTICATING KEY: [${username}]...`);
            await new Promise(r => setTimeout(r, 450));

            await login({ email: username, password, remember });

            setAuthStep('ACCESS GRANTED // INITIALIZING QUANTUM SESSION...');
            
            // Dramatic exit animation into dashboard
            if (cardRef.current && emblemRef.current) {
                gsap.to(emblemRef.current, { scale: 1.25, filter: 'drop-shadow(0 0 35px #10b981)', duration: 0.6 });
                gsap.to(cardRef.current, { scale: 0.98, opacity: 0.2, filter: 'blur(6px)', duration: 0.5 });
            }

            await new Promise(r => setTimeout(r, 400));
            navigate('/admin/dashboard');
        } catch (err: any) {
            setIsAuthenticating(false);
            setAuthStep('');
            setError(err.message || 'Access Denied: Invalid credentials.');

            // Error glitch shake animation
            if (cardRef.current) {
                gsap.fromTo(cardRef.current, 
                    { x: -12 }, 
                    { x: 12, duration: 0.08, repeat: 5, yoyo: true, ease: 'none', onComplete: () => {
                        gsap.set(cardRef.current, { x: 0 });
                    }}
                );
            }
        }
    };

    return (
        <div ref={containerRef} className="relative min-h-screen bg-slate-950 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8 font-sans text-slate-100 overflow-hidden select-none">
            
            {/* Background Canvas: Interactive Constellation Particles */}
            <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />

            {/* Glowing Ambient Light Orbs */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] rounded-full bg-emerald-500/10 filter blur-[100px] pointer-events-none animate-pulse-aura z-0" />
            <div className="absolute top-2/3 right-1/4 w-[400px] h-[400px] rounded-full bg-teal-500/10 filter blur-[90px] pointer-events-none z-0" />

            {/* High-Tech Perspective Grid Floor Effect */}
            <div className="absolute inset-0 bg-grid-pattern opacity-15 pointer-events-none z-0" />

            {/* Main Center Content */}
            <div className="relative z-10 w-full max-w-md">

                {/* Cybernetic Rotating Emblem */}
                <div ref={emblemRef} className="flex flex-col items-center mb-6">
                    <div className="relative w-28 h-28 flex items-center justify-center">
                        
                        {/* Outer Rotating Cybernetic Ring */}
                        <svg className="absolute inset-0 w-full h-full animate-spin-slow text-emerald-500/40" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="47" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="4 6" />
                            <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(16, 185, 129, 0.7)" strokeWidth="2" strokeDasharray="12 88" />
                            <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(20, 184, 166, 0.7)" strokeWidth="2" strokeDasharray="12 88" strokeDashoffset="50" />
                        </svg>

                        {/* Inner Counter-Rotating Ring */}
                        <svg className="absolute inset-2 w-[calc(100%-16px)] h-[calc(100%-16px)] animate-spin-reverse-slow text-teal-400/50" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="44" fill="none" stroke="currentColor" strokeWidth="1.2" strokeDasharray="8 12" />
                            <polygon points="50,4 53,10 47,10" fill="currentColor" />
                            <polygon points="50,96 53,90 47,90" fill="currentColor" />
                            <polygon points="4,50 10,53 10,47" fill="currentColor" />
                            <polygon points="96,50 90,53 90,47" fill="currentColor" />
                        </svg>

                        {/* Central Glowing Portrait Logo */}
                        <div className="relative w-20 h-20 rounded-full overflow-hidden p-1 shadow-[0_0_25px_rgba(16,185,129,0.4)] ring-2 ring-emerald-500/80 bg-slate-900 z-10">
                            <img src="/Rakib's logo.jpg" alt="Rakib's Care" className="w-full h-full object-cover rounded-full" />
                        </div>
                    </div>
                </div>

                {/* Header Titles & Security Telemetry Badges */}
                <div ref={titleRef} className="text-center space-y-2 mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/90 border border-emerald-500/30 text-[10px] font-mono uppercase tracking-widest text-emerald-400 shadow-lg shadow-emerald-950/40">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        <span>Gateway // Enclave Protocol: /rakib69</span>
                    </div>

                    <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                        Rakib's <span className="text-emerald-400">Care</span>
                    </h1>
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
                        Administrative Command Portal
                    </p>
                </div>

                {/* Login Glassmorphism Card */}
                <div 
                    ref={cardRef} 
                    className="relative rounded-3xl p-8 sm:p-10 glass-card shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] border border-slate-800/90 overflow-hidden backdrop-blur-xl"
                >
                    {/* Laser Scanline Beam Animation */}
                    <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-400/80 to-transparent shadow-[0_0_15px_#10b981] animate-scanline pointer-events-none" />

                    {/* Corner Tech Accents */}
                    <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-emerald-500/50" />
                    <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-emerald-500/50" />
                    <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-emerald-500/50" />
                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-emerald-500/50" />

                    {/* Error Box */}
                    {error && (
                        <div className="mb-6 p-4 rounded-xl bg-rose-950/80 border border-rose-500/50 text-rose-200 text-xs font-semibold flex items-center gap-2.5 animate-shake">
                            <svg className="w-4 h-4 text-rose-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Form Controls */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        {/* Username Input */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-widest">
                                    Operator Identifier
                                </label>
                                <span className="text-[10px] font-mono text-slate-500">CLEARANCE: ROOT</span>
                            </div>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    required 
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    disabled={isAuthenticating}
                                    className="w-full px-4 py-3 rounded-xl bg-slate-950/90 border border-slate-800 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm font-semibold tracking-wide transition"
                                    placeholder="Enter Operator ID or Email" 
                                />
                                <div className="absolute right-3.5 top-3.5 text-slate-500 pointer-events-none">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Password Input (Masked & Invisible) */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-widest">
                                    Security Key
                                </label>
                                <span className="text-[10px] font-mono text-emerald-400/80">ENCRYPTED</span>
                            </div>
                            <div className="relative">
                                <input 
                                    type={showPassword ? 'text' : 'password'} 
                                    required 
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    disabled={isAuthenticating}
                                    className="w-full px-4 py-3 pr-12 rounded-xl bg-slate-950/90 border border-slate-800 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm font-semibold tracking-widest transition"
                                    placeholder="••••••••••••"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                    className="absolute right-3 top-3 p-1 text-slate-500 hover:text-emerald-400 transition cursor-pointer"
                                    title={showPassword ? 'Hide security key' : 'Show security key'}
                                    aria-label={showPassword ? 'Hide security key' : 'Show security key'}
                                >
                                    {showPassword ? (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Session Memory */}
                        <div className="flex items-center justify-between text-xs pt-1">
                            <label className="flex items-center gap-2.5 cursor-pointer text-slate-400 hover:text-slate-200 transition">
                                <input 
                                    type="checkbox" 
                                    checked={remember}
                                    onChange={e => setRemember(e.target.checked)}
                                    className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500 cursor-pointer"
                                />
                                <span>Persist 30-Day Authorized Session</span>
                            </label>
                        </div>

                        {/* Dramatic Authenticate Button */}
                        <div>
                            <button 
                                type="submit" 
                                disabled={isAuthenticating}
                                className={`w-full py-3.5 px-4 rounded-xl text-xs font-bold uppercase tracking-widest bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white shadow-xl shadow-emerald-950/60 transition duration-300 cursor-pointer relative overflow-hidden group ${isAuthenticating ? 'opacity-85 pointer-events-none' : ''}`}
                            >
                                <span className="relative z-10 flex items-center justify-center gap-2">
                                    {isAuthenticating ? (
                                        <>
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            <span className="font-mono text-[11px]">{authStep}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Authorize Access</span>
                                            <span className="group-hover:translate-x-1 transition duration-300">&rarr;</span>
                                        </>
                                    )}
                                </span>
                                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition duration-300" />
                            </button>
                        </div>
                    </form>

                    {/* Terminal Footer Telemetry */}
                    <div className="mt-6 pt-4 border-t border-slate-800/80 flex items-center justify-between text-[10px] font-mono text-slate-500">
                        <span className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                            NODE: CLOUDFLARE EDGE
                        </span>
                        <span>TLS 1.3 // 256-BIT</span>
                    </div>
                </div>

                {/* Return Link */}
                <div className="mt-8 text-center">
                    <a href="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-emerald-400 transition">
                        <span>&larr;</span>
                        <span>Return to Rakib's Care Official Website</span>
                    </a>
                </div>

            </div>
        </div>
    );
};
